import { useCallback, useMemo, useRef, useState } from "react";
import {
  Panel,
  Group as PanelGroup,
  Separator as PanelResizeHandle,
} from "react-resizable-panels";
import { toast } from "sonner";

import { getEncounterReportPdf } from "@elation/api-client/src/stitch-web";

import { useIsInElationEHR } from "@src/features/iframe";
import { VisitTour } from "@src/features/tourguide";
import { VerticalFlexbox } from "@src/shared/components/layout/Flexbox";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@src/shared/components/ui/tabs";
import { useContainerWiderThan } from "@src/shared/hooks/useContainerWiderThan";
import { getServerErrorMessage } from "@src/shared/utils/http/errors";

import { EncounterReportEntity } from "../../../../application/encounter-report.entity";
import { useEncounterNoteSave } from "../../../../application/useEncounterNoteSave";
import type { PatientSelectorPatientSummaryV2 } from "../PatientSelector";
import { VisitNoteEditor } from "../VisitNoteEditor";
import { CompletedVisitActionItemsPanel } from "./components/CompletedVisitActionItemsPanel";
import { CompletedVisitAgendaPanel } from "./components/CompletedVisitAgendaPanel";
import { CompletedVisitCodesCollapsiblePanel } from "./components/CompletedVisitCodesPanel";
import { CompletedVisitHeader } from "./components/CompletedVisitHeader";
import { CompletedVisitTranscriptPanel } from "./components/CompletedVisitTranscriptPanel";
import {
  type DockPlacement,
  PanelDockToggle,
} from "./components/PanelDockToggle";
import {
  NOTE_MIN_HEIGHT,
  NOTE_MIN_WIDTH,
  NOTE_PANEL_ID,
  PANEL_GROUP_CLASS,
  RAIL_DEFAULT_WIDTH,
  RAIL_FILLER_ID,
  RAIL_MIN_WIDTH,
  RAIL_SIDE_BY_SIDE_MIN_WIDTH,
  RESIZE_TARGET_MINIMUM_SIZE,
  ResizeGrip,
  renderStackMember,
  resizeHandleClass,
} from "./PanelStackShell";
import {
  type DockedPanelHeights,
  type DockedPanelId,
  type DockedPanelOpenState,
  FILLER_MIN_HEIGHT,
  PANEL_DEFAULT_HEIGHT,
  usePanelStack,
} from "./usePanelStack";

const DOCKED_PANEL_IDS = [
  "agenda",
  "codes",
] as const satisfies readonly DockedPanelId[];

/**
 * Side each panel stacks on when it leaves the rail: the agenda goes above the
 * note and the codes below it, matching the design.
 */
const STACKED_SIDE: Record<DockedPanelId, "top" | "bottom"> = {
  agenda: "top",
  codes: "bottom",
};

const TAB_TRIGGER_CLASS =
  "h-7 rounded-md px-2.5 text-[12px] data-[state=active]:bg-primary/10 data-[state=active]:text-primary data-[state=active]:shadow-none";

export interface CompletedVisitProps {
  patient?: PatientSelectorPatientSummaryV2 | null;
  patientId?: number;
  noteText?: string | null;
  noteRevision?: number;
  encounterId?: number;
  /** Visit crumb from `EncounterEntity.visitDateTimeLabel`. */
  visitLabel?: string | null;
  scratchpadNotes?: string | null;
  /**
   * When set, the header shows Back on the same row as the patient info
   * (patient-details pattern) so the route can drop its separate back chrome.
   */
  backTo?: string;
}

export function CompletedVisit({
  encounterId,
  noteText,
  noteRevision = 0,
  patient,
  patientId,
  visitLabel,
  scratchpadNotes,
  backTo,
}: CompletedVisitProps) {
  const { save: saveNote } = useEncounterNoteSave(encounterId, {
    patientId,
  });
  const isInElationEHR = useIsInElationEHR();
  const [agendaOpen, setAgendaOpen] = useState(true);
  const [codesOpen, setCodesOpen] = useState(true);
  const [placement, setPlacement] = useState<
    Record<DockedPanelId, DockPlacement>
  >({ agenda: "right", codes: "right" });
  const [isGeneratingReport, setIsGeneratingReport] = useState(false);
  const shellElementRef = useRef<HTMLDivElement | null>(null);
  const isSideBySide = useContainerWiderThan(
    shellElementRef,
    RAIL_SIDE_BY_SIDE_MIN_WIDTH,
  );
  const isGeneratingReportRef = useRef(false);
  const expandedHeightsRef = useRef<DockedPanelHeights>({
    agenda: PANEL_DEFAULT_HEIGHT,
    codes: PANEL_DEFAULT_HEIGHT,
  });

  const open = useMemo<DockedPanelOpenState>(
    () => ({ agenda: agendaOpen, codes: codesOpen }),
    [agendaOpen, codesOpen],
  );

  // Docking to the right needs a rail beside the note, so it only applies once
  // the area is wide enough. Below that everything stacks in the note column.
  const agendaInRail = isSideBySide && placement.agenda === "right";
  const codesInRail = isSideBySide && placement.codes === "right";
  const hasRail = agendaInRail || codesInRail;

  const railMembers = useMemo(
    () =>
      DOCKED_PANEL_IDS.filter((id) =>
        id === "agenda" ? agendaInRail : codesInRail,
      ),
    [agendaInRail, codesInRail],
  );
  const stackedMembers = useMemo(
    () =>
      DOCKED_PANEL_IDS.filter((id) =>
        id === "agenda" ? !agendaInRail : !codesInRail,
      ),
    [agendaInRail, codesInRail],
  );

  const railStack = usePanelStack({
    members: railMembers,
    open,
    heightsRef: expandedHeightsRef,
  });
  const noteStack = usePanelStack({
    members: stackedMembers,
    open,
    heightsRef: expandedHeightsRef,
  });

  const setPanelOpen = useCallback((id: DockedPanelId, nextOpen: boolean) => {
    if (id === "agenda") setAgendaOpen(nextOpen);
    else setCodesOpen(nextOpen);
  }, []);

  const togglePanelPlacement = useCallback((id: DockedPanelId) => {
    setPlacement((current) => ({
      ...current,
      [id]: current[id] === "right" ? "stacked" : "right",
    }));
  }, []);

  const downloadEncounterReport = useCallback(async () => {
    if (encounterId == null || isGeneratingReportRef.current) return;

    isGeneratingReportRef.current = true;
    setIsGeneratingReport(true);
    const loadingToastId = toast.loading("Generating encounter report...");

    try {
      const { data: pdf } = await getEncounterReportPdf(encounterId);
      EncounterReportEntity.download(pdf, `encounter-${encounterId}.pdf`);
      toast.success("Encounter report downloaded.", { id: loadingToastId });
    } catch (error) {
      toast.dismiss(loadingToastId);
      toast.error(
        getServerErrorMessage(error) ??
          "Unable to download the encounter report. Please try again.",
      );
    } finally {
      isGeneratingReportRef.current = false;
      setIsGeneratingReport(false);
    }
  }, [encounterId]);

  const dockToggleFor = (id: DockedPanelId) =>
    isSideBySide ? (
      <PanelDockToggle
        panelName={id}
        placement={placement[id]}
        stackedSide={STACKED_SIDE[id]}
        onToggle={() => togglePanelPlacement(id)}
      />
    ) : null;

  const panelContentFor = (id: DockedPanelId) =>
    id === "agenda" ? (
      <CompletedVisitAgendaPanel
        encounterId={encounterId}
        open={agendaOpen}
        onOpenChange={(nextOpen) => setPanelOpen("agenda", nextOpen)}
        scratchpadNotes={scratchpadNotes}
        actions={dockToggleFor("agenda")}
      />
    ) : (
      <VisitTour.CodesStep className="flex h-full min-h-0">
        <CompletedVisitCodesCollapsiblePanel
          encounterId={encounterId}
          open={codesOpen}
          onOpenChange={(nextOpen) => setPanelOpen("codes", nextOpen)}
          compact
          actions={dockToggleFor("codes")}
        />
      </VisitTour.CodesStep>
    );

  const noteEditor = (
    <VisitTour.NoteStep className="flex h-full min-h-0">
      <VisitNoteEditor
        value={noteText}
        noteRevision={noteRevision}
        ariaLabel="Completed visit note editor"
        minHeightClassName="min-h-0"
        onPrint={downloadEncounterReport}
        printLabel="Download report"
        printDisabled={encounterId == null}
        isPrinting={isGeneratingReport}
        onSave={encounterId != null ? saveNote : undefined}
        showLocalSaveNotice={!isInElationEHR}
      />
    </VisitTour.NoteStep>
  );

  /**
   * Both groups stay mounted whether or not they currently hold a docked panel.
   * Swapping between two tree shapes would remount `VisitNoteEditor` on every
   * dock toggle and throw away the clinician's unsaved edits and undo history.
   */
  const noteColumn = (
    <PanelGroup
      orientation="vertical"
      className={PANEL_GROUP_CLASS}
      elementRef={noteStack.elementRef}
      resizeTargetMinimumSize={RESIZE_TARGET_MINIMUM_SIZE}
    >
      {stackedMembers
        .filter((id) => STACKED_SIDE[id] === "top")
        .flatMap((id) =>
          renderStackMember({
            id,
            stack: noteStack,
            handleSide: "after",
            isOpen: open[id],
            children: panelContentFor(id),
          }),
        )}

      <Panel
        id={NOTE_PANEL_ID}
        minSize={NOTE_MIN_HEIGHT}
        className="outline-none"
      >
        {noteEditor}
      </Panel>

      {stackedMembers
        .filter((id) => STACKED_SIDE[id] === "bottom")
        .flatMap((id) =>
          renderStackMember({
            id,
            stack: noteStack,
            handleSide: "before",
            isOpen: open[id],
            children: panelContentFor(id),
          }),
        )}
    </PanelGroup>
  );

  return (
    <VerticalFlexbox className="h-full min-h-0 w-full bg-background">
      <CompletedVisitHeader
        patient={patient}
        visitLabel={visitLabel}
        backTo={backTo}
      />

      {/*
        Measure width on a host outside the tab panels. Side-by-side docking
        depends on this width staying stable while the clinician moves between
        Note / Action Items / Transcript.
      */}
      <div
        ref={shellElementRef}
        className="flex h-full min-h-0 flex-1 flex-col"
      >
        <Tabs
          defaultValue="note"
          className="flex h-full min-h-0 flex-1 flex-col"
        >
          <TabsList className="h-9 justify-start rounded-none border-border border-b bg-background px-[18px] py-0 md-stitch:px-6">
            <TabsTrigger value="note" className={TAB_TRIGGER_CLASS}>
              Note
            </TabsTrigger>
            <TabsTrigger value="actionItems" className={TAB_TRIGGER_CLASS}>
              Action Items
            </TabsTrigger>
            <TabsTrigger value="transcript" className={TAB_TRIGGER_CLASS}>
              Transcript
            </TabsTrigger>
          </TabsList>

          {/*
            Keep every tab panel in the same absolutely-positioned slot so the
            Note PanelGroups never go through `display: none`. Hidden groups
            measure as 0×0 and react-resizable-panels then clamps panels back
            to minSize — which is why leaving for Action Items / Transcript
            used to wipe the clinician's layout.
          */}
          <div className="relative min-h-0 flex-1">
            <TabsContent
              value="note"
              forceMount
              className="absolute inset-0 mt-0 flex min-h-0 flex-col overflow-hidden focus-visible:outline-none focus-visible:ring-0 focus-visible:ring-offset-0 data-[state=inactive]:pointer-events-none data-[state=inactive]:invisible data-[state=inactive]:!flex [&[hidden]]:!flex [&_*]:focus-visible:outline-none [&_*]:focus-visible:ring-0 [&_*]:focus-visible:ring-offset-0"
            >
              <div className="h-full min-h-0 px-[18px] py-3 md-stitch:px-6">
                <PanelGroup
                  orientation="horizontal"
                  className={PANEL_GROUP_CLASS}
                  resizeTargetMinimumSize={RESIZE_TARGET_MINIMUM_SIZE}
                >
                  <Panel
                    id="note-column"
                    minSize={hasRail ? NOTE_MIN_WIDTH : 0}
                    className="outline-none"
                  >
                    {noteColumn}
                  </Panel>

                  {hasRail ? (
                    <PanelResizeHandle
                      id="rail-resize-handle"
                      className={resizeHandleClass(true, "x")}
                      aria-label="Resize rail"
                    >
                      <ResizeGrip orientation="x" />
                    </PanelResizeHandle>
                  ) : null}

                  {hasRail ? (
                    <Panel
                      id="visit-rail"
                      defaultSize={RAIL_DEFAULT_WIDTH}
                      minSize={RAIL_MIN_WIDTH}
                      groupResizeBehavior="preserve-pixel-size"
                    >
                      <PanelGroup
                        orientation="vertical"
                        className={PANEL_GROUP_CLASS}
                        elementRef={railStack.elementRef}
                        resizeTargetMinimumSize={RESIZE_TARGET_MINIMUM_SIZE}
                      >
                        {railMembers.flatMap((id) =>
                          renderStackMember({
                            id,
                            stack: railStack,
                            handleSide: "after",
                            isOpen: open[id],
                            children: panelContentFor(id),
                          }),
                        )}

                        {/* Its `minSize` is what caps how tall a rail panel can grow. */}
                        <Panel
                          id={RAIL_FILLER_ID}
                          minSize={FILLER_MIN_HEIGHT}
                        />
                      </PanelGroup>
                    </Panel>
                  ) : null}
                </PanelGroup>
              </div>
            </TabsContent>

            <TabsContent
              value="actionItems"
              className="absolute inset-0 mt-0 min-h-0 px-[18px] py-3 md-stitch:px-6"
            >
              <CompletedVisitActionItemsPanel
                encounterId={encounterId}
                collapsible={false}
              />
            </TabsContent>

            <TabsContent
              value="transcript"
              className="absolute inset-0 mt-0 min-h-0"
            >
              <CompletedVisitTranscriptPanel encounterId={encounterId} />
            </TabsContent>
          </div>
        </Tabs>
      </div>
    </VerticalFlexbox>
  );
}
